import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

// ─── Stage inference (product decision 2026-04-29) ───────────────────────────
function inferStages(a: { assessment_status: string; transition_analysis: any }) {
  const s1 = a.assessment_status === 'completed';
  const s2 = a.transition_analysis != null;
  const active = s1 && s2;
  return {
    s1: s1 ? 'Completed' : 'Pending',
    s2: s2 ? 'Completed' : s1 ? 'In Progress' : '—',
    s3: active ? 'Active' : '—',
    s4: active ? 'Active' : '—',
  };
}

// Cohort summary text for institution admin PDF
function instCohortSummary(
  name: string, total: number, s1: number, s2: number,
  active: number, weekly: number, archetypes: Record<string, number>
): string {
  const p = (n: number) => total ? `${Math.round(n / total * 100)}%` : '0%';
  const topArch = Object.entries(archetypes).sort((a, b) => b[1] - a[1])[0];
  const profileWord = topArch
    ? ({ 'The Commander': 'leadership-forward', 'The Architect': 'systems-thinking', 'The Catalyst': 'catalyst-driven' } as any)[topArch[0]] || 'results-oriented'
    : null;
  const l1 = `${name} has ${total} athlete${total !== 1 ? 's' : ''} enrolled on the BTHD platform, with ${s1} (${p(s1)}) completing the Identity Assessment and ${s2} (${p(s2)}) receiving a personalized Transition Analysis.`;
  const l2 = topArch && profileWord
    ? `The ${topArch[0]} archetype is most prevalent at ${s1 ? Math.round(topArch[1] / s1 * 100) : 0}% of assessed athletes, reflecting a ${profileWord} cohort profile.`
    : s1 > 0 ? 'Archetype data is being collected as athletes progress through the Identity Assessment.'
    : 'Encourage athletes to begin the BTHD journey by completing their Identity Assessment.';
  const l3 = active > 0
    ? `${active} athlete${active !== 1 ? 's are' : ' is'} actively engaged across all four BTHD stages, with ${weekly} showing platform activity in the past 7 days.`
    : weekly > 0 ? `${weekly} athlete${weekly !== 1 ? 's are' : ' is'} active this week as the cohort progresses through the assessment stages.`
    : 'Encourage athletes to complete the Identity Assessment to unlock their personalized transition and leadership pathway.';
  return `${l1} ${l2} ${l3}`;
}

// ─── Generic institution progress PDF ────────────────────────────────────────
function openGenericProgressPDF(institutionName: string, institutionType: string, athletes: any[]) {
  const today = new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
  const total = athletes.length;
  const s1Count = athletes.filter(a => a.assessment_status === 'completed').length;
  const s2Count = athletes.filter(a => a.transition_analysis != null).length;
  const activeCount = athletes.filter(a => a.assessment_status === 'completed' && a.transition_analysis != null).length;
  const sevenAgo = Date.now() - 7 * 86400000;
  const weeklyActive = athletes.filter(a => a.updated_at && new Date(a.updated_at).getTime() > sevenAgo).length;
  const pct = (n: number) => total ? `${Math.round(n / total * 100)}%` : '—';
  const archetypes: Record<string, number> = {};
  athletes.forEach(a => { if (a.athlete_archetype) archetypes[a.athlete_archetype] = (archetypes[a.athlete_archetype] || 0) + 1; });
  const summary = instCohortSummary(institutionName, total, s1Count, s2Count, activeCount, weeklyActive, archetypes);

  const badge = (s: string) => {
    if (s === 'Completed')   return `<span class="b bg">✓ Completed</span>`;
    if (s === 'Active')      return `<span class="b bb">● Active</span>`;
    if (s === 'In Progress') return `<span class="b bo">In Progress</span>`;
    return `<span style="color:#94a3b8">—</span>`;
  };

  const athleteRows = athletes.map(a => {
    const st = inferStages(a);
    const lastActive = a.updated_at ? new Date(a.updated_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '—';
    return `<tr>
      <td><strong>${(a.full_name || '—').slice(0, 28)}</strong></td>
      <td>${a.sport || '—'}</td>
      <td>${a.career_stage?.replace(/_/g, ' ') || '—'}</td>
      <td>${a.athlete_archetype || '—'}</td>
      <td style="text-align:center">${badge(st.s1)}</td>
      <td style="text-align:center">${badge(st.s2)}</td>
      <td style="text-align:center">${badge(st.s3)}</td>
      <td style="text-align:center">${badge(st.s4)}</td>
      <td style="color:#64748b;font-size:11px">${lastActive}</td>
    </tr>`;
  }).join('');

  const stageRows = [
    { label: '01 — Identity Assessment',    count: s1Count,    note: 'Completed', cls: 'bg' },
    { label: '02 — Transition Analysis',    count: s2Count,    note: 'Completed', cls: 'bg' },
    { label: '03 — Leadership Development', count: activeCount, note: 'Active',   cls: 'bb' },
    { label: '04 — Legacy Building',        count: activeCount, note: 'Active',   cls: 'bb' },
  ].map(s => `<tr>
    <td>${s.label}</td>
    <td style="text-align:center;font-weight:700">${s.count}</td>
    <td style="text-align:center">${pct(s.count)}</td>
    <td style="text-align:center"><span class="b ${s.cls}">${s.note}</span></td>
  </tr>`).join('');

  const html = `<!DOCTYPE html><html lang="en"><head>
<meta charset="UTF-8"><title>BTHD Progress Report — ${institutionName}</title>
<link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Barlow:wght@400;600;700&display=swap" rel="stylesheet">
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Barlow',sans-serif;color:#0a0f1a;background:#fff;padding:40px 48px;font-size:13px;line-height:1.5}
.hd{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:3px solid #bf1e2e;margin-bottom:28px}
.brand{font-family:'Barlow Condensed',sans-serif;font-weight:900;font-size:10px;color:#bf1e2e;letter-spacing:3px;text-transform:uppercase;margin-bottom:6px}
h1{font-family:'Barlow Condensed',sans-serif;font-weight:900;font-size:28px;color:#0a0f1a;line-height:1.1;margin-bottom:2px}
h2{font-family:'Barlow Condensed',sans-serif;font-weight:800;font-size:13px;color:#0a0f1a;letter-spacing:1.5px;text-transform:uppercase}
.meta{text-align:right;font-size:12px;color:#64748b;line-height:2}
.meta strong{color:#0a0f1a}
.sg{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:24px}
.sc{border:1px solid #e2e8f0;border-radius:10px;padding:14px 16px;border-top:3px solid #bf1e2e}
.sv{font-family:'Barlow Condensed',sans-serif;font-weight:900;font-size:30px;color:#0a0f1a;line-height:1;margin-bottom:4px}
.sl{font-size:9px;font-weight:700;color:#64748b;letter-spacing:1px;text-transform:uppercase}
.sec{margin-bottom:24px}
.sh{display:flex;align-items:center;gap:10px;padding-bottom:8px;border-bottom:2px solid #0a0f1a;margin-bottom:12px}
.dot{width:8px;height:8px;border-radius:50%;background:#bf1e2e;flex-shrink:0}
table{width:100%;border-collapse:collapse}
th{font-size:9px;font-weight:700;color:#94a3b8;letter-spacing:1.5px;text-transform:uppercase;padding:7px 10px;border-bottom:2px solid #e2e8f0;text-align:left}
td{padding:7px 10px;border-bottom:1px solid #f1f5f9;font-size:12px;vertical-align:middle}
tr:last-child td{border-bottom:none}tr:nth-child(even) td{background:#f8fafc}
.b{display:inline-block;padding:2px 7px;border-radius:20px;font-size:10px;font-weight:700}
.bg{background:#dcfce7;color:#16a34a}.bb{background:#dbeafe;color:#1d4ed8}.bo{background:#fed7aa;color:#c2410c}
.cb{background:#f8fafc;border:1px solid #e2e8f0;border-left:4px solid #014c8d;border-radius:8px;padding:14px 18px;margin-bottom:24px;font-size:13px;line-height:1.8;color:#334155}
.ft{margin-top:32px;padding-top:14px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:10px;color:#94a3b8}
.print-btn{position:fixed;top:20px;right:20px;background:#bf1e2e;color:#fff;border:none;padding:11px 22px;border-radius:8px;font-family:'Barlow',sans-serif;font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 4px 12px rgba(191,30,46,0.3)}
@media print{.print-btn{display:none}body{padding:20px 24px}.pb{page-break-before:always}}
</style></head><body>
<button class="print-btn" onclick="window.print()">↓ Save as PDF</button>
<div class="hd">
  <div>
    <div class="brand">Bigger Than Hoop Dreams — Progress Report</div>
    <h1>${institutionName}</h1>
    <div style="font-size:13px;color:#64748b;margin-top:4px">${institutionType.replace(/_/g, ' ')}</div>
  </div>
  <div class="meta">
    <div>Report Date: <strong>${today}</strong></div>
    <div>Athletes Enrolled: <strong>${total}</strong></div>
    <div>Active This Week: <strong>${weeklyActive}</strong></div>
  </div>
</div>
<div class="sg">
  <div class="sc"><div class="sv">${total}</div><div class="sl">Total Athletes</div></div>
  <div class="sc"><div class="sv" style="color:#bf1e2e">${s1Count}</div><div class="sl">Identity Assessed</div></div>
  <div class="sc"><div class="sv" style="color:#014c8d">${s2Count}</div><div class="sl">Transition Analysis</div></div>
  <div class="sc"><div class="sv" style="color:#0fa86d">${activeCount}</div><div class="sl">Fully Engaged</div></div>
</div>
${total > 0 ? `<div class="cb">${summary}</div>` : ''}
<div class="sec">
  <div class="sh"><div class="dot"></div><h2>Stage Completion Overview</h2></div>
  <table><thead><tr><th>Stage</th><th style="text-align:center">Athletes</th><th style="text-align:center">% of Cohort</th><th style="text-align:center">Status</th></tr></thead>
  <tbody>${stageRows}</tbody></table>
</div>
${athletes.length > 0 ? `<div class="sec ${athletes.length > 14 ? 'pb' : ''}">
  <div class="sh"><div class="dot"></div><h2>Individual Athlete Progress</h2></div>
  <table><thead><tr><th>Name</th><th>Sport</th><th>Stage</th><th>Archetype</th><th style="text-align:center">Stage 01</th><th style="text-align:center">Stage 02</th><th style="text-align:center">Stage 03</th><th style="text-align:center">Stage 04</th><th>Last Active</th></tr></thead>
  <tbody>${athleteRows}</tbody></table>
</div>` : ''}
<div class="ft">
  <span>BTHD — Bigger Than Hoop Dreams · bthd.app</span>
  <span>Confidential · Generated ${today}</span>
</div>
</body></html>`;

  const win = window.open('', '_blank', 'width=1100,height=800');
  if (!win) { alert('Enable pop-ups to open the report.'); return; }
  win.document.write(html);
  win.document.close();
}

// ─── School progress PDF (student_athletes + student_progress) ───────────────
function openSchoolProgressPDF(institutionName: string, institutionType: string, students: any[], progress: any[]) {
  const today = new Date().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' });
  const total = students.length;
  const getProgress = (id: string) => progress.find((p: any) => p.student_id === id);
  const sevenAgo = Date.now() - 7 * 86400000;

  const s1Count = progress.filter((p: any) => p.stage_1_completed).length;
  const s2Count = progress.filter((p: any) => p.stage_2_completed).length;
  const s3Count = progress.filter((p: any) => p.stage_3_completed).length;
  const s4Count = progress.filter((p: any) => p.stage_4_completed).length;
  const weeklyActive = progress.filter((p: any) => p.last_active && new Date(p.last_active).getTime() > sevenAgo).length;
  const activeCount = progress.filter((p: any) => p.stage_1_completed && p.stage_2_completed).length;
  const pct = (n: number) => total ? `${Math.round(n / total * 100)}%` : '—';

  const cohort = `${institutionName} has ${total} student athlete${total !== 1 ? 's' : ''} enrolled, with ${s1Count} (${pct(s1Count)}) completing Stage 01 and ${s2Count} (${pct(s2Count)}) completing Stage 02. ${weeklyActive} student${weeklyActive !== 1 ? 's are' : ' is'} actively engaged on the platform in the past 7 days.`;

  const stageBadge = (done: boolean | undefined, s1done: boolean, s2done: boolean, stageNum: number) => {
    if (done) return `<span class="b bg">✓ Completed</span>`;
    if (stageNum >= 3 && s1done && s2done) return `<span class="b bb">● Active</span>`;
    if (stageNum <= 2 && (stageNum === 1 || s1done)) return `<span class="b bo">In Progress</span>`;
    return `<span style="color:#94a3b8">—</span>`;
  };

  const studentRows = students.map((s: any) => {
    const prog = getProgress(s.id);
    const lastActive = prog?.last_active ? new Date(prog.last_active).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '—';
    const s1done = !!prog?.stage_1_completed;
    const s2done = !!prog?.stage_2_completed;
    return `<tr>
      <td><strong>${(s.full_name || '—').slice(0, 28)}</strong></td>
      <td>${s.sport || '—'}</td>
      <td>${s.grade || '—'}</td>
      <td style="text-align:center">${stageBadge(prog?.stage_1_completed, s1done, s2done, 1)}</td>
      <td style="text-align:center">${stageBadge(prog?.stage_2_completed, s1done, s2done, 2)}</td>
      <td style="text-align:center">${stageBadge(prog?.stage_3_completed, s1done, s2done, 3)}</td>
      <td style="text-align:center">${stageBadge(prog?.stage_4_completed, s1done, s2done, 4)}</td>
      <td style="color:#64748b;font-size:11px">${lastActive}</td>
    </tr>`;
  }).join('');

  const stageRows = [
    { label: '01 — Athlete Assessment',     count: s1Count, cls: 'bg', note: 'Completed' },
    { label: '02 — Skills & Pathways',       count: s2Count, cls: 'bg', note: 'Completed' },
    { label: '03 — Leadership Development',  count: s3Count > 0 ? s3Count : activeCount, cls: s3Count > 0 ? 'bg' : 'bb', note: s3Count > 0 ? 'Completed' : 'Active' },
    { label: '04 — Lasting Impact',          count: s4Count > 0 ? s4Count : activeCount, cls: s4Count > 0 ? 'bg' : 'bb', note: s4Count > 0 ? 'Completed' : 'Active' },
  ].map(s => `<tr>
    <td>${s.label}</td>
    <td style="text-align:center;font-weight:700">${s.count}</td>
    <td style="text-align:center">${pct(s.count)}</td>
    <td style="text-align:center"><span class="b ${s.cls}">${s.note}</span></td>
  </tr>`).join('');

  const html = `<!DOCTYPE html><html lang="en"><head>
<meta charset="UTF-8"><title>BTHD Progress Report — ${institutionName}</title>
<link href="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700;800;900&family=Barlow:wght@400;600;700&display=swap" rel="stylesheet">
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Barlow',sans-serif;color:#0a0f1a;background:#fff;padding:40px 48px;font-size:13px;line-height:1.5}
.hd{display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:20px;border-bottom:3px solid #bf1e2e;margin-bottom:28px}
.brand{font-family:'Barlow Condensed',sans-serif;font-weight:900;font-size:10px;color:#bf1e2e;letter-spacing:3px;text-transform:uppercase;margin-bottom:6px}
h1{font-family:'Barlow Condensed',sans-serif;font-weight:900;font-size:28px;line-height:1.1;margin-bottom:2px}
h2{font-family:'Barlow Condensed',sans-serif;font-weight:800;font-size:13px;letter-spacing:1.5px;text-transform:uppercase}
.meta{text-align:right;font-size:12px;color:#64748b;line-height:2}.meta strong{color:#0a0f1a}
.sg{display:grid;grid-template-columns:repeat(4,1fr);gap:14px;margin-bottom:24px}
.sc{border:1px solid #e2e8f0;border-radius:10px;padding:14px 16px;border-top:3px solid #bf1e2e}
.sv{font-family:'Barlow Condensed',sans-serif;font-weight:900;font-size:30px;line-height:1;margin-bottom:4px}
.sl{font-size:9px;font-weight:700;color:#64748b;letter-spacing:1px;text-transform:uppercase}
.sec{margin-bottom:24px}
.sh{display:flex;align-items:center;gap:10px;padding-bottom:8px;border-bottom:2px solid #0a0f1a;margin-bottom:12px}
.dot{width:8px;height:8px;border-radius:50%;background:#bf1e2e;flex-shrink:0}
table{width:100%;border-collapse:collapse}
th{font-size:9px;font-weight:700;color:#94a3b8;letter-spacing:1.5px;text-transform:uppercase;padding:7px 10px;border-bottom:2px solid #e2e8f0;text-align:left}
td{padding:7px 10px;border-bottom:1px solid #f1f5f9;font-size:12px;vertical-align:middle}
tr:last-child td{border-bottom:none}tr:nth-child(even) td{background:#f8fafc}
.b{display:inline-block;padding:2px 7px;border-radius:20px;font-size:10px;font-weight:700}
.bg{background:#dcfce7;color:#16a34a}.bb{background:#dbeafe;color:#1d4ed8}.bo{background:#fed7aa;color:#c2410c}
.cb{background:#f8fafc;border:1px solid #e2e8f0;border-left:4px solid #014c8d;border-radius:8px;padding:14px 18px;margin-bottom:24px;font-size:13px;line-height:1.8;color:#334155}
.ft{margin-top:32px;padding-top:14px;border-top:1px solid #e2e8f0;display:flex;justify-content:space-between;font-size:10px;color:#94a3b8}
.print-btn{position:fixed;top:20px;right:20px;background:#bf1e2e;color:#fff;border:none;padding:11px 22px;border-radius:8px;font-family:'Barlow',sans-serif;font-size:13px;font-weight:700;cursor:pointer;box-shadow:0 4px 12px rgba(191,30,46,0.3)}
@media print{.print-btn{display:none}body{padding:20px 24px}.pb{page-break-before:always}}
</style></head><body>
<button class="print-btn" onclick="window.print()">↓ Save as PDF</button>
<div class="hd">
  <div>
    <div class="brand">Bigger Than Hoop Dreams — Student Athlete Progress Report</div>
    <h1>${institutionName}</h1>
    <div style="font-size:13px;color:#64748b;margin-top:4px">${institutionType.replace(/_/g, ' ')}</div>
  </div>
  <div class="meta">
    <div>Report Date: <strong>${today}</strong></div>
    <div>Students Enrolled: <strong>${total}</strong></div>
    <div>Active This Week: <strong>${weeklyActive}</strong></div>
  </div>
</div>
<div class="sg">
  <div class="sc"><div class="sv">${total}</div><div class="sl">Total Students</div></div>
  <div class="sc"><div class="sv" style="color:#bf1e2e">${s1Count}</div><div class="sl">Stage 01 Complete</div></div>
  <div class="sc"><div class="sv" style="color:#014c8d">${s2Count}</div><div class="sl">Stage 02 Complete</div></div>
  <div class="sc"><div class="sv" style="color:#0fa86d">${weeklyActive}</div><div class="sl">Active This Week</div></div>
</div>
<div class="cb">${cohort}</div>
<div class="sec">
  <div class="sh"><div class="dot"></div><h2>Stage Completion Overview</h2></div>
  <table><thead><tr><th>Stage</th><th style="text-align:center">Students</th><th style="text-align:center">% of Cohort</th><th style="text-align:center">Status</th></tr></thead>
  <tbody>${stageRows}</tbody></table>
</div>
${students.length > 0 ? `<div class="sec ${students.length > 14 ? 'pb' : ''}">
  <div class="sh"><div class="dot"></div><h2>Individual Student Progress</h2></div>
  <table><thead><tr><th>Name</th><th>Sport</th><th>Grade</th><th style="text-align:center">Stage 01</th><th style="text-align:center">Stage 02</th><th style="text-align:center">Stage 03</th><th style="text-align:center">Stage 04</th><th>Last Active</th></tr></thead>
  <tbody>${studentRows}</tbody></table>
</div>` : ''}
<div class="ft">
  <span>BTHD — Bigger Than Hoop Dreams · bthd.app</span>
  <span>Confidential · Generated ${today}</span>
</div>
</body></html>`;

  const win = window.open('', '_blank', 'width=1100,height=800');
  if (!win) { alert('Enable pop-ups to open the report.'); return; }
  win.document.write(html);
  win.document.close();
}

interface Props {
  institutionType: string;
  institutionId: string | null;
  institutionName?: string;
}

interface QuickStat { label: string; value: string | number; sub?: string; color?: string; }

// ─── Content map per institution type ────────────────────────────────────────

function getConfig(type: string): {
  title: string;
  subtitle: string;
  accentColor: string;
  sections: string[];
} {
  const SCHOOL_TYPES = ['high_school', 'prep_school', 'aau', 'college', 'university'];
  if (SCHOOL_TYPES.includes(type)) return {
    title: 'School Dashboard',
    subtitle: 'Athlete roster, assessment progress, and NIL readiness',
    accentColor: '#014c8d',
    sections: ['roster', 'assessment', 'nil', 'academic'],
  };
  if (type === 'sports_org') return {
    title: 'Sports Organization Dashboard',
    subtitle: 'Pro athlete roster and transition readiness scores',
    accentColor: '#bf1e2e',
    sections: ['roster', 'transition', 'archetype'],
  };
  if (type === 'player_association') return {
    title: 'Player Association Dashboard',
    subtitle: 'Member transition stats and financial readiness overview',
    accentColor: '#0fa86d',
    sections: ['members', 'transition', 'financial'],
  };
  if (type === 'booster') return {
    title: 'Booster Dashboard',
    subtitle: 'Program impact metrics and athlete engagement',
    accentColor: '#e88c0e',
    sections: ['impact', 'engagement', 'assessment'],
  };
  if (type === 'agent') return {
    title: 'Agent Dashboard',
    subtitle: 'Client roster, transition progress, and career stage overview',
    accentColor: '#6b46c1',
    sections: ['clients', 'transition', 'archetype'],
  };
  if (type === 'corporation') return {
    title: 'Talent Pipeline',
    subtitle: 'Anonymized athlete talent pool by sport, stage, and archetype',
    accentColor: '#014c8d',
    sections: ['talent', 'stats'],
  };
  if (type === 'wealth_management') return {
    title: 'Financial Readiness Overview',
    subtitle: 'Athletes approaching retirement and financial transition readiness',
    accentColor: '#0fa86d',
    sections: ['readiness', 'stats'],
  };
  return {
    title: 'Dashboard',
    subtitle: '',
    accentColor: '#bf1e2e',
    sections: ['roster'],
  };
}

const SECTION_LABELS: Record<string, string> = {
  roster:     'Athlete Roster',
  assessment: 'Assessment Completion',
  nil:        'NIL Readiness',
  academic:   'Academic Balance',
  transition: 'Transition Readiness',
  archetype:  'Archetype Distribution',
  financial:  'Financial Readiness',
  impact:     'Program Impact',
  engagement: 'Athlete Engagement',
  members:    'Member Overview',
  clients:    'Client Roster',
};

// ─── Placeholder section cards ────────────────────────────────────────────────

function SectionCard({ title, color, children }: { title: string; color: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 12, overflow: 'hidden',
    }}>
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid var(--border)',
        borderTop: `3px solid ${color}`,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
        <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 14, color: 'var(--text)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
          {title}
        </span>
      </div>
      <div style={{ padding: '18px 20px' }}>{children}</div>
    </div>
  );
}

function StatRow({ label, value, sub, color = '#fff' }: QuickStat) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <div>
        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 22, fontWeight: 900, color }}>{value}</div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

// ─── Corporation view ────────────────────────────────────────────────────────

function CorporationView({ institutionId }: { institutionId: string | null }) {
  const [athletes, setAthletes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalAthlete, setModalAthlete] = useState<number | null>(null);

  useEffect(() => {
    let q = supabase.from('profiles').select('id,sport,career_stage,athlete_archetype,assessment_status')
      .eq('is_admin', false).eq('user_type', 'athlete');
    if (institutionId) q = q.eq('institution_id', institutionId);
    q.then(({ data }) => { setAthletes(data ?? []); setLoading(false); });
  }, [institutionId]);

  const total = athletes.length;
  const retiring = athletes.filter(a => a.career_stage === 'retiring').length;
  const assessed = athletes.filter(a => a.assessment_status === 'completed').length;

  return (
    <div style={{ padding: '28px 28px 60px', maxWidth: 1000 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#014c8d' }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)' }}>corporation</span>
        </div>
        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 28, fontWeight: 900, color: 'var(--text)', marginBottom: 4 }}>Talent Pipeline</div>
        <div style={{ fontSize: 14, color: 'var(--text-2)' }}>Anonymized athlete talent pool by sport, stage, and archetype</div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
        {[
          { label: 'Total Athletes in Platform', value: total,    color: '#014c8d' },
          { label: 'Retiring Athletes',           value: retiring, color: '#e88c0e' },
          { label: 'Completed Assessments',       value: assessed, color: '#0fa86d' },
        ].map((s, i) => (
          <div key={i} style={{
            flex: '1 1 140px', background: 'var(--surface)', borderRadius: 12,
            padding: '16px 18px', border: '1px solid var(--border)',
            borderTop: `3px solid ${s.color}`,
          }}>
            <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 30, fontWeight: 900, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4, fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ color: 'var(--text-3)', fontSize: 14 }}>Loading talent data…</div>
      ) : (
        <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', borderTop: '3px solid #014c8d', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#014c8d' }} />
            <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 14, color: 'var(--text)', letterSpacing: 0.5, textTransform: 'uppercase' }}>Talent Pool</span>
          </div>
          {athletes.length === 0 ? (
            <div style={{ padding: 24, fontSize: 13, color: 'var(--text-3)', fontStyle: 'italic' }}>No athletes found in platform.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Athlete</th>
                  <th>Sport</th>
                  <th>Career Stage</th>
                  <th>Archetype</th>
                  <th>Assessment</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {athletes.map((a, i) => (
                  <tr key={a.id}>
                    <td style={{ fontWeight: 600, color: 'var(--text-2)' }}>Athlete #{i + 1}</td>
                    <td>{a.sport || '—'}</td>
                    <td style={{ textTransform: 'capitalize' }}>{a.career_stage?.replace(/_/g, ' ') || '—'}</td>
                    <td>{a.athlete_archetype || '—'}</td>
                    <td>
                      <span style={{
                        padding: '3px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        color: a.assessment_status === 'completed' ? '#0fa86d' : '#e88c0e',
                        border: `1.5px solid ${a.assessment_status === 'completed' ? '#0fa86d' : '#e88c0e'}55`,
                        background: `${a.assessment_status === 'completed' ? '#0fa86d' : '#e88c0e'}11`,
                      }}>
                        {a.assessment_status === 'completed' ? 'Completed' : 'Pending'}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => setModalAthlete(i + 1)}
                        style={{
                          padding: '5px 12px', borderRadius: 6, background: 'transparent',
                          border: '1px solid var(--border)', color: 'var(--text-2)',
                          fontSize: 11, fontWeight: 600, cursor: 'pointer',
                          fontFamily: 'Barlow, sans-serif',
                        }}
                      >
                        Request Introduction
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modal */}
      {modalAthlete !== null && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setModalAthlete(null)}>
          <div style={{
            background: 'var(--surface)', borderRadius: 14, padding: 32,
            border: '1px solid var(--border)', maxWidth: 380, width: '90%',
            textAlign: 'center',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 18, color: 'var(--text)', marginBottom: 12 }}>
              Introduction Request
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.7, marginBottom: 24 }}>
              To request an introduction to Athlete #{modalAthlete}, contact us at{' '}
              <a href="mailto:harry@bthd.app" style={{ color: '#bf1e2e', fontWeight: 700 }}>harry@bthd.app</a>.
            </div>
            <button
              onClick={() => setModalAthlete(null)}
              style={{
                padding: '10px 24px', borderRadius: 8, background: '#bf1e2e',
                border: 'none', color: '#fff', fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 700, fontSize: 14, cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Wealth Management view ──────────────────────────────────────────────────

function WealthManagementView({ institutionId }: { institutionId: string | null }) {
  const [athletes, setAthletes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showConsultModal, setShowConsultModal] = useState(false);

  useEffect(() => {
    let q = supabase
      .from('profiles')
      .select('id,sport,career_stage,athlete_archetype,assessment_status,last_analysis_at')
      .eq('is_admin', false).eq('user_type', 'athlete')
      .in('career_stage', ['retiring', 'professional']);
    if (institutionId) q = q.eq('institution_id', institutionId);
    q.then(({ data }) => { setAthletes(data ?? []); setLoading(false); });
  }, [institutionId]);

  const retiring = athletes.filter(a => a.career_stage === 'retiring').length;
  const assessed = athletes.filter(a => a.assessment_status === 'completed').length;
  const total = athletes.length;
  const completionRate = total ? Math.round((assessed / total) * 100) : 0;

  const daysSince = (dateStr: string | null): string => {
    if (!dateStr) return '—';
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
    return `${diff}d ago`;
  };

  return (
    <div style={{ padding: '28px 28px 60px', maxWidth: 1000 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#0fa86d' }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)' }}>wealth management</span>
        </div>
        <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 28, fontWeight: 900, color: 'var(--text)', marginBottom: 4 }}>Financial Readiness Overview</div>
        <div style={{ fontSize: 14, color: 'var(--text-2)' }}>Athletes approaching retirement and financial transition readiness</div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
        {[
          { label: 'Athletes Approaching Retirement', value: retiring,          color: '#e88c0e' },
          { label: 'Total Platform Athletes',          value: total,             color: '#0fa86d' },
          { label: 'Assessment Completion Rate',       value: `${completionRate}%`, color: '#014c8d' },
        ].map((s, i) => (
          <div key={i} style={{
            flex: '1 1 140px', background: 'var(--surface)', borderRadius: 12,
            padding: '16px 18px', border: '1px solid var(--border)',
            borderTop: `3px solid ${s.color}`,
          }}>
            <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 30, fontWeight: 900, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4, fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ color: 'var(--text-3)', fontSize: 14 }}>Loading readiness data…</div>
      ) : (
        <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden', marginBottom: 24 }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', borderTop: '3px solid #0fa86d', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#0fa86d' }} />
            <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 14, color: 'var(--text)', letterSpacing: 0.5, textTransform: 'uppercase' }}>Transition-Ready Athletes</span>
          </div>
          {athletes.length === 0 ? (
            <div style={{ padding: 24, fontSize: 13, color: 'var(--text-3)', fontStyle: 'italic' }}>No retiring or professional athletes found.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Athlete</th>
                  <th>Career Stage</th>
                  <th>Sport</th>
                  <th>Archetype</th>
                  <th>Last Assessment</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {athletes.map((a, i) => (
                  <tr key={a.id}>
                    <td style={{ fontWeight: 600, color: 'var(--text-2)' }}>Athlete #{i + 1}</td>
                    <td style={{ textTransform: 'capitalize' }}>
                      <span style={{
                        padding: '3px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        color: a.career_stage === 'retiring' ? '#e88c0e' : '#0fa86d',
                        border: `1.5px solid ${a.career_stage === 'retiring' ? '#e88c0e' : '#0fa86d'}55`,
                        background: `${a.career_stage === 'retiring' ? '#e88c0e' : '#0fa86d'}11`,
                      }}>
                        {a.career_stage}
                      </span>
                    </td>
                    <td>{a.sport || '—'}</td>
                    <td>{a.athlete_archetype || '—'}</td>
                    <td style={{ color: 'var(--text-3)', fontSize: 12 }}>{daysSince(a.last_analysis_at)}</td>
                    <td>
                      <button
                        onClick={() => setShowConsultModal(true)}
                        style={{
                          padding: '5px 12px', borderRadius: 6, background: 'transparent',
                          border: '1px solid var(--border)', color: 'var(--text-2)',
                          fontSize: 11, fontWeight: 600, cursor: 'pointer',
                          fontFamily: 'Barlow, sans-serif',
                        }}
                      >
                        Schedule Consultation
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modal */}
      {showConsultModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setShowConsultModal(false)}>
          <div style={{
            background: 'var(--surface)', borderRadius: 14, padding: 32,
            border: '1px solid var(--border)', maxWidth: 380, width: '90%',
            textAlign: 'center',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 900, fontSize: 18, color: 'var(--text)', marginBottom: 12 }}>
              Schedule a Consultation
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.7, marginBottom: 24 }}>
              Contact us at{' '}
              <a href="mailto:harry@bthd.app" style={{ color: '#0fa86d', fontWeight: 700 }}>harry@bthd.app</a>{' '}
              to schedule a consultation with this athlete.
            </div>
            <button
              onClick={() => setShowConsultModal(false)}
              style={{
                padding: '10px 24px', borderRadius: 8, background: '#0fa86d',
                border: 'none', color: '#fff', fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 700, fontSize: 14, cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── School Student Dashboard (high_school / prep_school / aau) ──────────────

type SchoolTab = 'roster' | 'responses' | 'engagement';

function SchoolStudentDashboard({ institutionId, institutionName, institutionType }: {
  institutionId: string | null; institutionName?: string; institutionType: string;
}) {
  const [tab, setTab] = useState<SchoolTab>('roster');
  const [students, setStudents] = useState<any[]>([]);
  const [progress, setProgress] = useState<any[]>([]);
  const [responses, setResponses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStage, setFilterStage] = useState('');
  const [filterWeek, setFilterWeek] = useState('');
  const [filterStudent, setFilterStudent] = useState('');
  const [expandedResponse, setExpandedResponse] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      let sq = supabase.from('student_athletes').select('*');
      if (institutionId) sq = sq.eq('institution_id', institutionId);
      const { data: studs } = await sq;
      setStudents(studs ?? []);

      if (studs && studs.length > 0) {
        const ids = studs.map((s: any) => s.id);
        const { data: progs } = await supabase
          .from('student_progress').select('*').in('student_id', ids);
        setProgress(progs ?? []);

        const { data: resps } = await supabase
          .from('student_responses')
          .select('*, student_tasks(title, task_type)')
          .in('student_id', ids)
          .order('submitted_at', { ascending: false });
        setResponses(resps ?? []);
      }
      setLoading(false);
    };
    load();
  }, [institutionId]);

  const getProgress = (studentId: string) => progress.find((p: any) => p.student_id === studentId);
  const getStudentName = (studentId: string) => students.find((s: any) => s.id === studentId)?.full_name || '—';

  const daysSince = (d: string | null) => {
    if (!d) return '—';
    const diff = Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
    return diff === 0 ? 'Today' : `${diff}d ago`;
  };

  const totalStudents = students.length;
  const activeStudents = progress.filter((p: any) => {
    if (!p.last_active) return false;
    return (Date.now() - new Date(p.last_active).getTime()) < 7 * 86400000;
  }).length;
  const completedStage1 = progress.filter((p: any) => p.stage_1_completed).length;
  const totalResponses = responses.length;

  const TABS: { key: SchoolTab; label: string }[] = [
    { key: 'roster', label: 'Roster' },
    { key: 'responses', label: 'Responses' },
    { key: 'engagement', label: 'Engagement' },
  ];

  const filteredResponses = responses.filter((r: any) => {
    if (filterStage && String(r.stage) !== filterStage) return false;
    if (filterWeek && String(r.week) !== filterWeek) return false;
    if (filterStudent && !getStudentName(r.student_id).toLowerCase().includes(filterStudent.toLowerCase())) return false;
    return true;
  });

  return (
    <div style={{ padding: '28px 28px 60px', maxWidth: 1100 }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#014c8d' }} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)' }}>
              {institutionType.replace(/_/g, ' ')}
            </span>
          </div>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 28, fontWeight: 900, color: 'var(--text)', marginBottom: 4 }}>
            {institutionName || 'School Dashboard'}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-2)' }}>Athlete roster, weekly responses, and engagement metrics</div>
        </div>
        <button
          onClick={() => openSchoolProgressPDF(institutionName || 'School', institutionType, students, progress)}
        style={{
          padding: '9px 18px', borderRadius: 8, background: 'transparent',
          border: '1px solid var(--border)', color: 'var(--text-2)',
          fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700,
          fontSize: 14, cursor: 'pointer', letterSpacing: 0.5, flexShrink: 0, marginTop: 4,
        }}
        >
          ↓ Download Progress Report
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
        {[
          { label: 'Total Athletes',    value: totalStudents,     color: '#014c8d' },
          { label: 'Active (7 days)',    value: activeStudents,    color: '#0fa86d' },
          { label: 'Completed Stage 01', value: completedStage1,  color: '#e88c0e' },
          { label: 'Total Responses',   value: totalResponses,    color: '#bf1e2e' },
        ].map((s, i) => (
          <div key={i} style={{
            flex: '1 1 120px', background: 'var(--surface)', borderRadius: 12,
            padding: '16px 18px', border: '1px solid var(--border)', borderTop: `3px solid ${s.color}`,
          }}>
            <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 28, fontWeight: 900, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4, fontWeight: 600 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 18px', borderRadius: '8px 8px 0 0', fontSize: 13, fontWeight: 600,
              background: tab === t.key ? 'var(--surface)' : 'transparent',
              border: tab === t.key ? '1px solid var(--border)' : '1px solid transparent',
              borderBottom: tab === t.key ? '1px solid var(--surface)' : '1px solid transparent',
              color: tab === t.key ? 'var(--text)' : 'var(--text-2)',
              cursor: 'pointer', fontFamily: 'Barlow, sans-serif',
              marginBottom: tab === t.key ? -1 : 0,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-3)', fontSize: 14 }}>Loading athlete data…</div>
      ) : (
        <>
          {/* ── ROSTER TAB ── */}
          {tab === 'roster' && (
            <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', borderTop: '3px solid #014c8d', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#014c8d' }} />
                <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 14, color: 'var(--text)', letterSpacing: 0.5, textTransform: 'uppercase' }}>Athlete Roster</span>
              </div>
              {students.length === 0 ? (
                <div style={{ padding: 24, fontSize: 13, color: 'var(--text-3)', fontStyle: 'italic' }}>No athletes linked to this institution yet.</div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Sport</th>
                      <th>Grade</th>
                      <th>Current Stage</th>
                      <th>Current Week</th>
                      <th>Last Active</th>
                      <th>Stage 1</th>
                      <th>Stage 2</th>
                      <th>Stage 3</th>
                      <th>Stage 4</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s: any) => {
                      const prog = getProgress(s.id);
                      const stageLabel = (key: string) => prog?.[key]
                        ? <span style={{ color: '#0fa86d', fontWeight: 700 }}>✓</span>
                        : <span style={{ color: 'var(--text-3)' }}>—</span>;
                      return (
                        <tr key={s.id}>
                          <td style={{ fontWeight: 600 }}>{s.full_name}</td>
                          <td>{s.sport || '—'}</td>
                          <td>{s.grade || '—'}</td>
                          <td>{prog ? `Stage 0${prog.current_stage}` : '—'}</td>
                          <td>{prog ? `Week ${prog.current_week}` : '—'}</td>
                          <td style={{ color: 'var(--text-3)', fontSize: 12 }}>{daysSince(prog?.last_active)}</td>
                          <td style={{ textAlign: 'center' }}>{stageLabel('stage_1_completed')}</td>
                          <td style={{ textAlign: 'center' }}>{stageLabel('stage_2_completed')}</td>
                          <td style={{ textAlign: 'center' }}>{stageLabel('stage_3_completed')}</td>
                          <td style={{ textAlign: 'center' }}>{stageLabel('stage_4_completed')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ── RESPONSES TAB ── */}
          {tab === 'responses' && (
            <div>
              {/* Filters */}
              <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                <input
                  placeholder="Filter by athlete name..."
                  value={filterStudent}
                  onChange={e => setFilterStudent(e.target.value)}
                  style={{
                    padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)',
                    background: 'var(--surface)', color: 'var(--text)', fontSize: 13,
                    fontFamily: 'Barlow, sans-serif', minWidth: 200,
                  }}
                />
                <select
                  value={filterStage}
                  onChange={e => setFilterStage(e.target.value)}
                  style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13, fontFamily: 'Barlow, sans-serif' }}
                >
                  <option value="">All Stages</option>
                  <option value="1">Stage 01</option>
                  <option value="2">Stage 02</option>
                  <option value="3">Stage 03</option>
                  <option value="4">Stage 04</option>
                </select>
                <select
                  value={filterWeek}
                  onChange={e => setFilterWeek(e.target.value)}
                  style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 13, fontFamily: 'Barlow, sans-serif' }}
                >
                  <option value="">All Weeks</option>
                  <option value="1">Week 1</option>
                  <option value="2">Week 2</option>
                  <option value="3">Week 3</option>
                  <option value="4">Week 4</option>
                </select>
              </div>

              <div style={{ background: 'var(--surface)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
                <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', borderTop: '3px solid #014c8d', display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#014c8d' }} />
                  <span style={{ fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, fontSize: 14, color: 'var(--text)', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                    Athlete Responses ({filteredResponses.length})
                  </span>
                </div>
                {filteredResponses.length === 0 ? (
                  <div style={{ padding: 24, fontSize: 13, color: 'var(--text-3)', fontStyle: 'italic' }}>No responses yet.</div>
                ) : (
                  <div>
                    {filteredResponses.map((r: any) => (
                      <div key={r.id} style={{ borderBottom: '1px solid var(--border)', padding: '16px 18px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
                              {getStudentName(r.student_id)}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-3)', display: 'flex', gap: 8 }}>
                              <span>Stage 0{r.stage} · Week {r.week}</span>
                              <span>·</span>
                              <span>{r.student_tasks?.title || '—'}</span>
                              <span>·</span>
                              <span>{new Date(r.submitted_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <button
                            onClick={() => setExpandedResponse(expandedResponse === r.id ? null : r.id)}
                            style={{
                              padding: '4px 12px', borderRadius: 6, flexShrink: 0,
                              background: 'transparent', border: '1px solid var(--border)',
                              color: 'var(--text-2)', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                              fontFamily: 'Barlow, sans-serif',
                            }}
                          >
                            {expandedResponse === r.id ? 'Collapse' : 'Read'}
                          </button>
                        </div>
                        {expandedResponse === r.id && (
                          <div style={{
                            marginTop: 10, padding: '14px 16px',
                            background: 'var(--surface-2)', borderRadius: 8,
                            fontSize: 13, color: 'var(--text-2)', lineHeight: 1.75,
                            whiteSpace: 'pre-wrap',
                          }}>
                            {r.response}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── ENGAGEMENT TAB ── */}
          {tab === 'engagement' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
              {/* Active vs Inactive */}
              <SectionCard title="Activity Pulse" color="#014c8d">
                {[
                  { label: 'Active (last 7 days)',  value: activeStudents,               color: '#0fa86d' },
                  { label: 'Inactive',              value: totalStudents - activeStudents, color: '#e88c0e' },
                  { label: 'Total Athletes',         value: totalStudents,                color: 'var(--text)' },
                ].map((row, i) => <StatRow key={i} {...row} />)}
              </SectionCard>

              {/* Stage progression */}
              <SectionCard title="Stage Progression" color="#bf1e2e">
                {[1,2,3,4].map(stage => {
                  const key = `stage_${stage}_completed`;
                  const count = progress.filter((p: any) => p[key]).length;
                  const pct = totalStudents ? Math.round((count / totalStudents) * 100) : 0;
                  return (
                    <StatRow
                      key={stage}
                      label={`Stage 0${stage} Complete`}
                      value={count}
                      sub={`${pct}% of roster`}
                      color={count > 0 ? '#0fa86d' : 'var(--text-3)'}
                    />
                  );
                })}
              </SectionCard>

              {/* Weekly responses */}
              <SectionCard title="Response Volume" color="#e88c0e">
                {[1,2,3,4].map(stage => {
                  const count = responses.filter((r: any) => r.stage === stage).length;
                  return (
                    <StatRow key={stage} label={`Stage 0${stage} responses`} value={count} color="var(--text)" />
                  );
                })}
              </SectionCard>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function InstitutionDashboardPage({ institutionType, institutionId, institutionName }: Props) {
  const [athletes, setAthletes] = useState<any[]>([]);
  const [loading,  setLoading]  = useState(true);
  const cfg = getConfig(institutionType);

  const isPartnerType = institutionType === 'corporation' || institutionType === 'wealth_management';

  useEffect(() => {
    if (isPartnerType) { setLoading(false); return; }
    let q = supabase.from('profiles').select('id,full_name,sport,career_stage,athlete_archetype,assessment_status,transition_analysis,updated_at,institution_id')
      .eq('is_admin', false).eq('user_type', 'athlete');
    if (institutionId) q = q.eq('institution_id', institutionId);
    q.then(({ data }) => { setAthletes(data ?? []); setLoading(false); });
  }, [institutionId, isPartnerType]);

  // Route to dedicated views by institution type
  if (institutionType === 'corporation') return <CorporationView institutionId={institutionId} />;
  if (institutionType === 'wealth_management') return <WealthManagementView institutionId={institutionId} />;
  if (['high_school', 'prep_school', 'aau'].includes(institutionType)) {
    return <SchoolStudentDashboard institutionId={institutionId} institutionName={institutionName} institutionType={institutionType} />;
  }

  const assessed  = athletes.filter(a => a.assessment_status === 'completed').length;
  const pct       = athletes.length ? Math.round((assessed / athletes.length) * 100) : 0;
  const byArchetype: Record<string, number> = {};
  athletes.forEach(a => { if (a.athlete_archetype) byArchetype[a.athlete_archetype] = (byArchetype[a.athlete_archetype] || 0) + 1; });

  return (
    <div style={{ padding: '28px 28px 60px', maxWidth: 1100 }}>

      {/* Header */}
      <div style={{ marginBottom: 28, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: cfg.accentColor }} />
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)' }}>
              {institutionType.replace(/_/g, ' ')}
            </span>
          </div>
          <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 28, fontWeight: 900, color: 'var(--text)', marginBottom: 4 }}>
            {institutionName || cfg.title}
          </div>
          <div style={{ fontSize: 14, color: 'var(--text-2)' }}>{cfg.subtitle}</div>
        </div>
        {!loading && (
          <button
            onClick={() => openGenericProgressPDF(institutionName || cfg.title, institutionType, athletes)}
            style={{
              padding: '9px 18px', borderRadius: 8, background: 'transparent',
              border: '1px solid var(--border)', color: 'var(--text-2)',
              fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 700,
              fontSize: 14, cursor: 'pointer', letterSpacing: 0.5, marginTop: 4, flexShrink: 0,
            }}
          >
            ↓ Download Progress Report
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ color: 'var(--text-3)', fontSize: 14 }}>Loading athlete data…</div>
      ) : (
        <>
          {/* Quick stats row */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>
            {[
              { label: 'Total Athletes',   value: athletes.length,             color: cfg.accentColor },
              { label: 'Assessments Done', value: `${pct}%`,                   color: '#0fa86d' },
              { label: 'Assessed',         value: assessed,                    color: 'var(--text)' },
              { label: 'Not Assessed',     value: athletes.length - assessed,  color: '#e88c0e' },
            ].map((s, i) => (
              <div key={i} style={{
                flex: '1 1 120px', background: 'var(--surface)', borderRadius: 12,
                padding: '16px 18px', border: '1px solid var(--border)',
                borderTop: `3px solid ${s.color}`,
              }}>
                <div style={{ fontFamily: 'Barlow Condensed, sans-serif', fontSize: 28, fontWeight: 900, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4, fontWeight: 600 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Section grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>

            {/* Roster section */}
            <SectionCard title={SECTION_LABELS['roster']} color={cfg.accentColor}>
              {athletes.slice(0, 8).map((a, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < Math.min(7, athletes.length - 1) ? '1px solid var(--border)' : 'none' }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                    background: cfg.accentColor,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700, color: '#fff',
                  }}>
                    {(a.full_name || '?').split(' ').map((p: string) => p[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.full_name || 'Unknown'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{a.sport || '—'}{a.career_stage ? ` · ${a.career_stage}` : ''}</div>
                  </div>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: a.assessment_status === 'completed' ? '#0fa86d' : '#e88c0e',
                  }} />
                </div>
              ))}
              {athletes.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-3)', fontStyle: 'italic' }}>No athletes linked to this institution yet.</div>}
              {athletes.length > 8 && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 10, textAlign: 'center' }}>+{athletes.length - 8} more athletes</div>}
            </SectionCard>

            {/* Assessment + Archetype */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <SectionCard title="Assessment Progress" color="#0fa86d">
                {[
                  { label: 'Completed',    value: assessed,                    color: '#0fa86d' },
                  { label: 'In Progress',  value: athletes.length - assessed,  color: '#e88c0e' },
                  { label: 'Completion %', value: `${pct}%`,                   color: cfg.accentColor },
                ].map((row, i) => <StatRow key={i} {...row} />)}
              </SectionCard>

              {Object.keys(byArchetype).length > 0 && (
                <SectionCard title="Archetype Distribution" color="#bf1e2e">
                  {Object.entries(byArchetype).map(([arch, count], i) => (
                    <StatRow key={i} label={arch} value={count} sub={`${Math.round((count / athletes.length) * 100)}% of roster`} color="var(--text)" />
                  ))}
                </SectionCard>
              )}
            </div>
          </div>

          {/* Institution-type specific note */}
          <div style={{ marginTop: 24, padding: '14px 18px', background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)', fontSize: 13, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16 }}>ℹ️</span>
            <span>More {cfg.sections.slice(1).map(s => SECTION_LABELS[s] || s).join(', ')} analytics coming in the next sprint.</span>
          </div>
        </>
      )}
    </div>
  );
}
